import * as React from "react";

import {
    DropdownLabel,
    DropdownValue,
    Wrapper,
    IconWrapper,
    TranformableIconWrapper,
    ErrorMessage,
    DropdownValueItem,
} from "./glide-dropdown-button-style";
import { isEmptyOrUndefined } from "common/Support";
import { getNamedIcon, IconName } from "common/named-icons";
// import AppIcon from "common/app-icon";
import { ThemedIcon } from "designer/ui/views/themed-icon/themed-icon";
import AppIcon from "common/app-icon";
import { Tooltip } from "../../tooltip/tooltip";
import { GlideIcon } from "sharedUI/ui/views/glide-icon/glide-icon";

interface Props extends React.HTMLAttributes<HTMLButtonElement> {
    isOpen: boolean;
    value: { name: string; icon: React.ReactNode }[];
    warn?: boolean;
    warnString?: string;
    label?: string;
    icon?: React.ReactNode | string;
    drawBorder?: boolean;
    slim?: boolean;
    isEnabled?: boolean;
    isInline?: boolean;
    tallerVariation?: boolean;
}

const Chevron = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M8.41421 9C7.52331 9 7.07714 10.0771 7.70711 10.7071L11.2929 14.2929C11.6834 14.6834 12.3166 14.6834 12.7071 14.2929L16.2929 10.7071C16.9229 10.0771 16.4767 9 15.5858 9H8.41421Z"
            fill="currentColor"
        />
    </svg>
);

const GlideDropdownButton: React.VFC<Props> = p => {
    const {
        label,
        value,
        isOpen,
        icon,
        warn,
        drawBorder,
        onClick,
        slim,
        warnString,
        isEnabled,
        isInline,
        onMouseEnter,
        onMouseLeave,
        tallerVariation,
        ...rest
    } = p;

    const onClickInner = React.useCallback(
        (evnt: React.MouseEvent<HTMLButtonElement>) => {
            evnt.stopPropagation();
            onClick?.(evnt);
        },
        [onClick]
    );

    const renderIcon = (i: React.ReactNode | string | undefined) => {
        if (i === undefined) return undefined;
        if (typeof i === "string") {
            if (getNamedIcon(i as IconName) !== undefined) {
                return <ThemedIcon icon={i as IconName} />;
            } else {
                return <AppIcon size={16} icon={i} />;
            }
        } else {
            return i;
        }
    };

    const [hovered, setHovered] = React.useState(false);
    const onMouseEnterInner = React.useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            onMouseEnter?.(e);
            setHovered(true);
        },
        [onMouseEnter]
    );

    const onMouseLeaveInner = React.useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            onMouseLeave?.(e);
            setHovered(false);
            setShowTooltip(false);
        },
        [onMouseLeave]
    );

    const [showTooltip, setShowTooltip] = React.useState(false);
    React.useEffect(() => {
        if (hovered) {
            const h = window.setTimeout(() => setShowTooltip(true), 1500);
            return () => window.clearTimeout(h);
        }
        return undefined;
    }, [hovered]);

    const ref = React.useRef<HTMLDivElement>(null);

    const tooltipContent = value.map(i => i.name).join(" → ");

    return (
        <div ref={ref}>
            <Wrapper
                data-testid="glide-dropdown-button"
                drawBorder={drawBorder !== false}
                isOpen={isOpen}
                slim={slim === true}
                warn={warn === true}
                isEnabled={isEnabled !== false}
                isInline={isInline === true}
                tallerVariation={tallerVariation ?? false}
                onMouseEnter={onMouseEnterInner}
                onMouseLeave={onMouseLeaveInner}
                {...rest}
                onClick={onClickInner}>
                {!isEmptyOrUndefined(label) && <DropdownLabel>{label}</DropdownLabel>}
                {icon !== undefined && (
                    <IconWrapper tallerVariation={tallerVariation} isOpen={isOpen}>
                        {renderIcon(icon)}
                    </IconWrapper>
                )}
                <DropdownValue>
                    {value.map((v, i) => (
                        <DropdownValueItem key={i}>
                            {v.icon !== undefined && (
                                <IconWrapper tallerVariation={tallerVariation} isOpen={isOpen}>
                                    {renderIcon(v.icon)}
                                </IconWrapper>
                            )}
                            <div className="part-text" data-testid="glide-dropdown-button-part-text">
                                {v.name}
                            </div>
                            <div className="caret">
                                <GlideIcon kind="stroke" iconSize={16} icon="st-caret" rotateDeg={-90} />
                            </div>
                        </DropdownValueItem>
                    ))}
                </DropdownValue>
                <TranformableIconWrapper tallerVariation={tallerVariation}>
                    <Chevron />
                </TranformableIconWrapper>
            </Wrapper>
            <Tooltip target={ref} position="top" show={showTooltip}>
                {tooltipContent}
            </Tooltip>
            {warnString !== undefined && <ErrorMessage>{warnString}</ErrorMessage>}
        </div>
    );
};
export default GlideDropdownButton;
